if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();

        this.addEventListener('click', this.onDiscoverClick.bind(this));
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }

      get discover() {
        return this.querySelector('.search__discover');
      }

      get searchForm() {
        return this.querySelector('.search__form');
      }

      beforeShow() {
        super.beforeShow();
        this.resetDiscover();
        this.scheduleDiscoverEntrance();
      }

      afterHide() {
        super.afterHide();
        clearTimeout(this.discoverTimer);
        this.resetDiscover();
      }

      // afterShow = 75ms (active) + overlay transitionend (~0.8s). Start 100ms before that.
      scheduleDiscoverEntrance() {
        clearTimeout(this.discoverTimer);

        if (theme.config.motionReduced) {
          this.discoverTimer = setTimeout(() => this.animateDiscover(), 0);
          return;
        }

        const ACTIVE_DELAY = 75;
        const BEFORE_AFTER_SHOW = 100;
        let durationMs = 800;
        let delayMs = 0;

        if (this.overlay) {
          const style = getComputedStyle(this.overlay);
          durationMs = this.parseCssTime(style.transitionDuration) || durationMs;
          delayMs = this.parseCssTime(style.transitionDelay) || 0;
        }

        const wait = Math.max(0, ACTIVE_DELAY + delayMs + durationMs - BEFORE_AFTER_SHOW);
        this.discoverTimer = setTimeout(() => this.animateDiscover(), wait);
      }

      parseCssTime(value) {
        return Math.max(
          0,
          ...String(value || '0')
            .split(',')
            .map((part) => {
              const n = parseFloat(part);
              if (Number.isNaN(n)) return 0;
              return part.trim().endsWith('ms') ? n : n * 1000;
            })
        );
      }

      onDiscoverClick(event) {
        const termButton = event.target.closest('[data-search-term]');
        if (!termButton || !this.contains(termButton)) return;

        event.preventDefault();
        const term = termButton.getAttribute('data-search-term');
        if (!term || !this.input) return;

        this.input.value = term;
        this.input.focus();

        const form = this.searchForm;
        if (form?.hideTypewriter) form.hideTypewriter();

        if (form?.matches?.('[is="predictive-search"]')) {
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }

        form?.submit();
      }

      get discoverItems() {
        return this.discover
          ? Array.from(this.discover.querySelectorAll('.search__discover-term, .search__discover-product'))
          : [];
      }

      resetDiscover() {
        const discover = this.discover;
        if (!discover) return;

        discover.removeAttribute('data-visible');
        this.discoverItems.forEach((item) => {
          item.style.opacity = '';
          item.style.transform = '';
        });
      }

      animateDiscover() {
        const discover = this.discover;
        if (!discover || !this.open || this.input?.value?.trim()) return;

        const items = this.discoverItems;
        if (!items.length) return;

        if (theme.config.motionReduced) {
          discover.setAttribute('data-visible', '');
          return;
        }

        Motion.animate(
          items,
          { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
          { duration: 0.35, easing: [0.22, 1, 0.36, 1], delay: Motion.stagger(0.05) }
        ).finished.then(() => {
          if (!this.open) return;
          items.forEach((item) => {
            item.style.opacity = '';
            item.style.transform = '';
          });
          discover.setAttribute('data-visible', '');
        });
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
